"use client";

import { useMemo, useState } from "react";
import axios from "axios";
import {
  FiCalendar,
  FiDollarSign,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import {
  TbAlertCircle,
  TbCalculator,
  TbCar,
  TbCheck,
  TbCurrencyDollar,
  TbInfoCircle,
  TbRoute,
  TbTruck,
} from "react-icons/tb";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../Components/ui/dialog";
import SecondaryButton from "../../Components/SecondaryButton";
import { toast } from "sonner";

function formatCurrency(value) {
  if (value === null || value === undefined || String(value) === "") {
    return "Pending";
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(number);
}

function formatDate(dateString) {
  if (!dateString) return "—";

  const safeDate =
    String(dateString).length === 10
      ? `${dateString}T12:00:00`
      : String(dateString).replace(" ", "T");

  const date = new Date(safeDate);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusLabel(status) {
  const value = String(status ?? "");

  if (value === "1") return "New";
  if (value === "2") return "Awaiting";
  if (value === "3") return "Declined";
  if (value === "4") return "Accepted";

  return value || "—";
}

function formatNumber(value, fractionDigits = 2) {
  const number = Number(value);

  if (Number.isNaN(number) || !Number.isFinite(number)) {
    return "N/A";
  }

  return number.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatPriceInputValue(value) {
  const number = Number(value);

  if (Number.isNaN(number) || !Number.isFinite(number)) {
    return "";
  }

  return number.toFixed(2);
}

function getGoogleMapsUrlFromResponse(responseData) {
  if (typeof responseData === "string") return responseData;

  return (
    responseData?.data?.url ||
    responseData?.data?.script_url ||
    responseData?.url ||
    responseData?.script_url ||
    ""
  );
}

function extractGoogleMapsApiKeyFromScriptUrl(scriptUrl) {
  try {
    const url = new URL(scriptUrl);
    return url.searchParams.get("key") || "";
  } catch {
    return "";
  }
}

function getVehicleTitle(vehicle) {
  const title = [vehicle?.year, vehicle?.make, vehicle?.model]
    .filter(Boolean)
    .join(" ");

  const operableTag = vehicle?.operable ? "(OP)" : "(INOP)";

  return `${title || "Vehicle"} ${operableTag}`.trim();
}

function getMainVehicle(quote) {
  return Array.isArray(quote?.vehicles) && quote.vehicles.length > 0
    ? quote.vehicles[0]
    : null;
}

function getTransportTypeMeta(transportType) {
  const normalizedType = String(transportType || "Open").trim().toLowerCase();

  if (normalizedType === "enclosed") {
    return {
      label: "Enclosed",
      icon: <TbCar className="text-blue-300" />,
      className: "border-blue-500/30 bg-blue-500/10 text-blue-200",
    };
  }

  return {
    label: "Open",
    icon: <TbTruck className="text-amber-300" />,
    className: "border-amber-500/30 bg-amber-500/10 text-amber-200",
  };
}

function getDistanceMilesFromQuote(quote, quoteResult) {
  const distanceData = quoteResult?.input_parameters?.distance || {};

  const candidates = [
    distanceData.effective_distance_miles,
    distanceData.distance_miles,
    quote?.effective_distance_miles,
    quote?.original_distance_miles,
    quote?.original?.effective_distance_miles,
    quote?.original?.original_distance_miles,
  ];

  for (const candidate of candidates) {
    const number = Number(candidate);

    if (!Number.isNaN(number) && Number.isFinite(number) && number > 0) {
      return number;
    }
  }

  return null;
}

function calculatePerMile(price, distanceMiles) {
  const priceNumber = Number(price);
  const distanceNumber = Number(distanceMiles);

  if (
    Number.isNaN(priceNumber) ||
    Number.isNaN(distanceNumber) ||
    !Number.isFinite(priceNumber) ||
    !Number.isFinite(distanceNumber) ||
    distanceNumber <= 0
  ) {
    return null;
  }

  return priceNumber / distanceNumber;
}

function buildQuotePricePayloadFromQuote(quote) {
  return {
    pickup_zipcode: String(quote?.pickup_zipcode || "").trim(),
    pickup_city: String(quote?.pickup_city || "").trim(),
    pickup_state: String(quote?.pickup_state || "").trim().toUpperCase(),
    delivery_zipcode: String(quote?.delivery_zipcode || "").trim(),
    delivery_city: String(quote?.delivery_city || "").trim(),
    delivery_state: String(quote?.delivery_state || "").trim().toUpperCase(),
    transport_type: quote?.transport_type || "Open",
    quote_vehicles: (quote?.vehicles || []).map((vehicle) => ({
      year: String(vehicle?.year || "").trim(),
      make: String(vehicle?.make || "").trim(),
      model: String(vehicle?.model || "").trim(),
      type:
        String(vehicle?.type || "sedan")
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, "") || "sedan",
      operable: vehicle?.operable ? 1 : 0,
    })),
  };
}

function buildEmbedDirectionsUrl(apiKey, quote) {
  if (!apiKey || !quote) return "";

  const origin = `${quote.pickup_city || ""}, ${quote.pickup_state || ""} ${
    quote.pickup_zipcode || ""
  }, USA`;

  const destination = `${quote.delivery_city || ""}, ${quote.delivery_state || ""} ${
    quote.delivery_zipcode || ""
  }, USA`;

  if (!origin.trim() || !destination.trim()) return "";

  const params = new URLSearchParams({
    key: apiKey,
    origin,
    destination,
    mode: "driving",
  });

  return `https://www.google.com/maps/embed/v1/directions?${params.toString()}`;
}

function PricePill({ label, value, tone = "amber" }) {
  const toneClasses = {
    green: "bg-green-500/10 text-green-300 border-green-500/30",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    red: "bg-red-500/10 text-red-300 border-red-500/30",
  }[tone];

  return (
    <div className={`rounded-xl border px-3 py-2 ${toneClasses}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] opacity-80">
        {label}
      </p>

      <p className="mt-1 text-base sm:text-lg font-bold text-white">
        {formatCurrency(value)}
      </p>
    </div>
  );
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function hasDefinedQuotePrices(quote) {
  return (
    hasValue(quote?.price_low) ||
    hasValue(quote?.price_medium) ||
    hasValue(quote?.price_high)
  );
}

function PendingPriceCard() {
  return (
    <div className="rounded-xl border border-dashed border-amber-500/40 bg-amber-500/10 px-4 py-4">
      <div className="flex items-start gap-3">
        <TbAlertCircle className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />

        <div>
          <p className="text-sm font-semibold text-amber-200">
            Quote price has not been defined yet.
          </p>

          <p className="text-xs text-amber-100/80 mt-1">
            Open this quote to calculate or manually define Low, Medium and High prices.
          </p>
        </div>
      </div>
    </div>
  );
}

function DetailBlock({ label, value }) {
  return (
    <div className="rounded-lg bg-black/20 border border-zinc-700 p-3">
      <p className="text-xs text-gray-400">{label}</p>

      <p className="mt-1 text-sm text-white font-semibold break-words">
        {value || "N/A"}
      </p>
    </div>
  );
}

export default function QuoteContainer({
  data = [],
  loading = false,
  apiOrigin = "https://new.cajuu.app",
  onQuoteUpdated = () => {},
}) {
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quotePrices, setQuotePrices] = useState({
    low: "",
    medium: "",
    high: "",
  });
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState("");
  const [mapError, setMapError] = useState("");

  const isModalOpen = useMemo(() => !!selectedQuote, [selectedQuote]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      "Screen-Name": "/Quotes",
      Authorization: `Bearer ${token}`,
    };
  };

  const distanceMiles = useMemo(
    () => getDistanceMilesFromQuote(selectedQuote, quoteResult),
    [selectedQuote, quoteResult]
  );

  const quotePerMile = useMemo(
    () => ({
      low: calculatePerMile(quotePrices.low, distanceMiles),
      medium: calculatePerMile(quotePrices.medium, distanceMiles),
      high: calculatePerMile(quotePrices.high, distanceMiles),
    }),
    [quotePrices, distanceMiles]
  );

  const quoteDistanceData = quoteResult?.input_parameters?.distance || {};
  const quotePricingDisclaimer =
    quoteResult?.pricing_disclaimer ||
    quoteResult?.pricing_adjustment?.disclaimer ||
    "";

  const mainVehicle = getMainVehicle(selectedQuote);
  const selectedTransportTypeMeta = getTransportTypeMeta(selectedQuote?.transport_type);
  const mapUrl = buildEmbedDirectionsUrl(googleMapsApiKey, selectedQuote);

  const hydratePriceInputsFromQuote = (quote) => {
    setQuotePrices({
      low: formatPriceInputValue(quote?.price_low),
      medium: formatPriceInputValue(quote?.price_medium ?? quote?.payment_rate),
      high: formatPriceInputValue(quote?.price_high),
    });
  };

  const loadGoogleMapsApiKey = async () => {
    if (googleMapsApiKey) return googleMapsApiKey;

    try {
      const response = await axios.get(`${apiOrigin}/api/google_maps_script_url`, {
        headers: getAuthHeaders(),
      });

      const scriptUrl = getGoogleMapsUrlFromResponse(response.data);
      const apiKey = extractGoogleMapsApiKeyFromScriptUrl(scriptUrl);

      if (!apiKey) {
        setMapError("Google Maps API key was not returned by the API.");
        return "";
      }

      setGoogleMapsApiKey(apiKey);

      return apiKey;
    } catch (error) {
      console.error("Error loading Google Maps API key:", error);
      setMapError("Unable to load Google Maps map for this quote.");

      return "";
    }
  };

  const calculateQuotePrices = async (quote) => {
    if (!quote) return;

    const payload = buildQuotePricePayloadFromQuote(quote);

    if (!payload.quote_vehicles.length) {
      toast.error("This quote has no vehicle to calculate prices.");
      return;
    }

    setQuoteLoading(true);
    setQuoteResult(null);

    try {
      const response = await axios.post(`${apiOrigin}/api/quote-prices`, payload, {
        headers: getAuthHeaders(),
      });

      const responseData = response.data || {};
      const totalPrices = responseData.total_prices || {};

      setQuoteResult(responseData);

      setQuotePrices((previousPrices) => ({
        low: formatPriceInputValue(totalPrices.low) || previousPrices.low,
        medium: formatPriceInputValue(totalPrices.medium) || previousPrices.medium,
        high: formatPriceInputValue(totalPrices.high) || previousPrices.high,
      }));

      if (responseData.manual_pricing_required) {
        toast.warning("Manual pricing is required for this quote.");
      } else {
        toast.success("Automatic quote price calculated successfully.");
      }
    } catch (error) {
      console.error("Error calculating quote price:", error);
      toast.error(
        "Unable to calculate automatic quote price. You can edit the prices manually."
      );
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleModalOpen = async (quote) => {
    setSelectedQuote(quote);
    setMapError("");
    hydratePriceInputsFromQuote(quote);
    await loadGoogleMapsApiKey();
    await calculateQuotePrices(quote);
  };

  const handleModalClose = () => {
    setSelectedQuote(null);
    setQuoteResult(null);
    setQuoteLoading(false);
    setQuoteSaving(false);
    setQuotePrices({
      low: "",
      medium: "",
      high: "",
    });
    setMapError("");
  };

  const updateQuotePrice = (field, value) => {
    setQuotePrices((previousPrices) => ({
      ...previousPrices,
      [field]: value,
    }));
  };

  const validateQuotePrices = () => {
    const low = Number(quotePrices.low);
    const medium = Number(quotePrices.medium);
    const high = Number(quotePrices.high);

    if (Number.isNaN(low) || low < 0) {
      toast.error("Low price must be a valid number.");
      return false;
    }

    if (Number.isNaN(medium) || medium < 0) {
      toast.error("Medium price must be a valid number.");
      return false;
    }

    if (Number.isNaN(high) || high < 0) {
      toast.error("High price must be a valid number.");
      return false;
    }

    return true;
  };

  const handleSaveQuotePrices = async () => {
    if (!selectedQuote || !validateQuotePrices()) return;

    setQuoteSaving(true);

    const payload = {
      price_low: Number(quotePrices.low),
      price_medium: Number(quotePrices.medium),
      price_high: Number(quotePrices.high),
      payment_rate: Number(quotePrices.medium),
    };

    try {
      const response = await axios.put(
        `${apiOrigin}/api/quote/${selectedQuote.id}`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );

      const updatedRaw = response.data?.data || response.data || {};

      const updatedQuote = {
        ...selectedQuote,
        ...updatedRaw,
        price_low: payload.price_low,
        price_medium: payload.price_medium,
        price_high: payload.price_high,
        payment_rate: payload.payment_rate,
      };

      setSelectedQuote(updatedQuote);
      onQuoteUpdated(updatedQuote);
      toast.success("Quote prices saved successfully.");
    } catch (error) {
      console.error("Error saving quote prices:", error);

      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Unable to save quote prices. Check if the backend update endpoint accepts price_low, price_medium and price_high."
      );
    } finally {
      setQuoteSaving(false);
    }
  };

  return (
    <div className="w-full bg-[#1c1b19] p-4 sm:p-6 md:p-8 rounded-xl shadow-sm border border-zinc-600">
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleModalClose()}>
        <DialogContent className="bg-[#1c1b19] border border-zinc-600 text-white w-[96vw] max-w-[96vw] h-[94vh] max-h-[94vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="p-4 sm:p-6 border-b border-zinc-600">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="flex items-center gap-2">
                <TbCalculator className="text-amber-500" />
                Edit Quote #{selectedQuote?.id}
              </span>

              {selectedQuote?.has_load_transport ? (
                <span className="w-fit text-xs px-3 py-1 rounded-full border border-emerald-600/40 bg-emerald-600/20 text-emerald-200">
                  Converted to Load
                </span>
              ) : (
                <span className="w-fit text-xs px-3 py-1 rounded-full border border-amber-600/40 bg-amber-600/20 text-amber-200">
                  Open Quote
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedQuote && (
            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              <div className="rounded-xl overflow-hidden border border-zinc-700 bg-zinc-900/60 mb-6">
                {mapUrl ? (
                  <iframe
                    title="Quote route map"
                    src={mapUrl}
                    className="w-full h-[260px] sm:h-[320px] lg:h-[380px] bg-zinc-900"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="w-full h-[260px] sm:h-[320px] lg:h-[380px] bg-zinc-900 flex items-center justify-center text-gray-400">
                    Loading map...
                  </div>
                )}

                {mapError && (
                  <div className="px-4 py-3 text-sm text-amber-300 bg-amber-500/10 border-t border-amber-500/30">
                    {mapError}
                  </div>
                )}
              </div>

              {quoteLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>

                  <p className="mt-4 text-gray-300 text-base">
                    Calculating automatic quote price...
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {quoteResult?.manual_pricing_required && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <TbAlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />

                        <div>
                          <h3 className="text-amber-300 font-semibold text-base">
                            Manual pricing required
                          </h3>

                          <p className="text-sm text-gray-300 mt-1">
                            The automatic calculation returned a manual pricing status. You can
                            still enter the prices manually and save them.
                          </p>

                          {Array.isArray(quoteResult?.reasons) &&
                            quoteResult.reasons.length > 0 && (
                              <p className="text-xs text-gray-400 mt-2">
                                Reason: {quoteResult.reasons.join(", ")}
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  )}

                  {quotePricingDisclaimer && (
                    <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <TbInfoCircle className="w-5 h-5 text-blue-300 mt-0.5 flex-shrink-0" />

                        <p className="text-sm text-blue-100">
                          {quotePricingDisclaimer}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-amber-500 mb-4 flex items-center gap-2">
                        <TbRoute />
                        Trip Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Origin</p>

                          <p className="text-white font-medium">
                            {selectedQuote.pickup_address}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400">Destination</p>

                          <p className="text-white font-medium">
                            {selectedQuote.delivery_address}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                          <DetailBlock
                            label="Distance"
                            value={
                              quoteDistanceData.effective_distance_text ||
                              quoteDistanceData.distance_text ||
                              selectedQuote.effective_distance_text ||
                              selectedQuote.original_distance_text ||
                              (distanceMiles
                                ? `${formatNumber(distanceMiles)} mi`
                                : "N/A")
                            }
                          />

                          <DetailBlock
                            label="Duration"
                            value={
                              quoteDistanceData.effective_driver_duration_text ||
                              quoteDistanceData.driver_duration_text ||
                              quoteDistanceData.duration_text ||
                              selectedQuote.effective_driver_duration_text ||
                              selectedQuote.original_driver_duration_text ||
                              selectedQuote.effective_duration_text ||
                              selectedQuote.original_duration_text ||
                              "N/A"
                            }
                          />
                        </div>

                        <DetailBlock
                          label="Transport Type"
                          value={selectedTransportTypeMeta.label}
                        />
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-green-500 mb-4 flex items-center gap-2">
                        <TbCar />
                        Vehicle Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Primary Vehicle</p>

                          <p className="text-white font-semibold text-base">
                            {mainVehicle ? getVehicleTitle(mainVehicle) : "N/A"}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <DetailBlock label="Type" value={mainVehicle?.type || "N/A"} />

                          <DetailBlock
                            label="Condition"
                            value={mainVehicle?.operable ? "Operable" : "Inoperable"}
                          />
                        </div>

                        {selectedQuote.vehicles?.length > 1 && (
                          <div>
                            <p className="text-gray-400 mb-2">Other Vehicles</p>

                            <div className="space-y-2">
                              {selectedQuote.vehicles.slice(1).map((vehicle, index) => (
                                <div
                                  key={vehicle.id || index}
                                  className="rounded-lg bg-black/20 border border-zinc-700 p-3"
                                >
                                  <p className="text-white font-medium">
                                    {getVehicleTitle(vehicle)}
                                  </p>

                                  <p className="text-xs text-gray-400 mt-1">
                                    {vehicle.type || "N/A"}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-blue-500 mb-4 flex items-center gap-2">
                        <FiUser />
                        Customer Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Name</p>

                          <p className="text-white font-medium">
                            {selectedQuote.name || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400">Email</p>

                          <p className="text-white font-medium break-all">
                            {selectedQuote.email || "N/A"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400">Phone</p>

                          <p className="text-white font-medium">
                            {selectedQuote.phone || "N/A"}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                          <DetailBlock
                            label="Created"
                            value={formatDate(selectedQuote.created_at)}
                          />

                          <DetailBlock
                            label="Pickup Date"
                            value={formatDate(selectedQuote.pickup_date)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 sm:p-5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <TbCurrencyDollar className="text-amber-500" />
                        Quote Prices
                      </h3>

                      <p className="text-xs text-gray-400">
                        {distanceMiles
                          ? `Using ${formatNumber(distanceMiles)} miles to calculate $/mi.`
                          : "Distance unavailable for $/mi calculation."}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-green-300 mb-1">
                          Low Price
                        </label>

                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                              $
                            </span>

                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.low}
                              onChange={(event) =>
                                updateQuotePrice("low", event.target.value)
                              }
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>

                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-green-500/10 text-green-300 border border-green-500/30">
                            {quotePerMile.low !== null
                              ? `$${formatNumber(quotePerMile.low)}/mi`
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-amber-300 mb-1">
                          Medium Price
                        </label>

                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                              $
                            </span>

                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.medium}
                              onChange={(event) =>
                                updateQuotePrice("medium", event.target.value)
                              }
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/30">
                            {quotePerMile.medium !== null
                              ? `$${formatNumber(quotePerMile.medium)}/mi`
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-red-300 mb-1">
                          High Price
                        </label>

                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                              $
                            </span>

                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.high}
                              onChange={(event) =>
                                updateQuotePrice("high", event.target.value)
                              }
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                          </div>

                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30">
                            {quotePerMile.high !== null
                              ? `$${formatNumber(quotePerMile.high)}/mi`
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t border-zinc-600 p-4 sm:p-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <SecondaryButton
              variant="outline"
              onClick={handleModalClose}
              className="text-white border-white hover:bg-white/10 justify-center"
              disabled={quoteSaving}
            >
              Close
            </SecondaryButton>

            <SecondaryButton
              onClick={handleSaveQuotePrices}
              className="bg-amber-600 text-black hover:bg-amber-700 justify-center"
              disabled={quoteLoading || quoteSaving}
            >
              {quoteSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <TbCheck className="w-4 h-4 mr-2" />
                  Save Prices
                </>
              )}
            </SecondaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex justify-between items-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-white">
          Available Quotes
        </h2>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>

          <p className="mt-2 text-gray-400">Loading quotes...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-white text-lg">No quotes found matching your filters.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          {data.map((quote) => {
            const primaryVehicle = getMainVehicle(quote);
            const transportTypeMeta = getTransportTypeMeta(quote.transport_type);

            return (
              <li
                onClick={() => handleModalOpen(quote)}
                key={quote.id}
                className="group border border-zinc-700 hover:border-amber-500 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-200 bg-[#181714] cursor-pointer overflow-hidden"
              >
                <div className="flex items-center justify-between gap-3 p-3 border-b border-zinc-700 bg-black/10">
                  <p className="text-xs sm:text-sm font-semibold text-white">
                    #{quote.id}
                  </p>

                  {quote.has_load_transport ? (
                    <span className="text-[11px] px-2 py-0 rounded-full border border-emerald-600/40 bg-emerald-600/20 text-emerald-200 whitespace-nowrap">
                      Load Created
                    </span>
                  ) : (
                    <span className="text-[11px] px-2 py-0 rounded-full border border-amber-600/40 bg-amber-600/20 text-amber-200 whitespace-nowrap">
                      Awaiting Conversion
                    </span>
                  )}
                </div>

                <div className="p-4 sm:p-5 space-y-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-lg sm:text-xl font-bold text-white leading-tight">
                        {primaryVehicle ? getVehicleTitle(primaryVehicle) : "No vehicle found"}
                      </p>

                      {quote.vehicles?.length > 1 && (
                        <p className="text-xs text-gray-400 mt-1">
                          +{quote.vehicles.length - 1} additional vehicle(s)
                        </p>
                      )}
                    </div>

                    <div
                      className={`w-fit flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full border ${transportTypeMeta.className}`}
                    >
                      {transportTypeMeta.icon}
                      <span>{transportTypeMeta.label}</span>
                    </div>
                  </div>

                  {hasDefinedQuotePrices(quote) ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <PricePill label="Low" value={quote.price_low} tone="green" />

                      <PricePill
                        label="Medium"
                        value={quote.price_medium ?? quote.payment_rate}
                        tone="amber"
                      />

                      <PricePill label="High" value={quote.price_high} tone="red" />
                    </div>
                  ) : (
                    <PendingPriceCard />
                  )}

                  <div className="rounded-xl border border-zinc-700 bg-black/20 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 md:gap-4">
                      <div className="flex items-center justify-start md:justify-center gap-3 min-w-0 text-left md:text-center">
                        <span className="flex h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-green-200 flex-shrink-0"></span>

                        <p className="font-medium leading-tight text-sm sm:text-base text-white truncate md:whitespace-nowrap">
                          {quote.pickup_address}
                        </p>
                      </div>

                      <div className="hidden md:flex items-center justify-center text-amber-400 font-bold text-lg px-1">
                        ➜
                      </div>

                      <div className="flex items-center justify-start md:justify-center gap-3 min-w-0 text-left md:text-center">
                        <span className="flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-red-200 flex-shrink-0"></span>

                        <p className="font-medium leading-tight text-sm sm:text-base text-white truncate md:whitespace-nowrap">
                          {quote.delivery_address}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl border border-zinc-700 bg-black/10 p-3 space-y-2">
                      <div className="flex items-center text-gray-300 min-w-0">
                        <FiUser className="mr-2 text-blue-400 flex-shrink-0" />

                        <span className="truncate">{quote.name || "—"}</span>
                      </div>

                      <div className="flex items-center text-gray-300 min-w-0">
                        <FiMail className="mr-2 text-blue-400 flex-shrink-0" />

                        <span className="truncate">{quote.email || "—"}</span>
                      </div>

                      <div className="flex items-center text-gray-300 min-w-0">
                        <FiPhone className="mr-2 text-blue-400 flex-shrink-0" />

                        <span className="truncate">{quote.phone || "—"}</span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-700 bg-black/10 p-3 space-y-2">
                      <div className="flex items-center text-gray-300">
                        <FiCalendar className="mr-2 text-amber-500 flex-shrink-0" />

                        <span>Created: {formatDate(quote.created_at)}</span>
                      </div>

                      <div className="flex items-center text-gray-300">
                        <FiMapPin className="mr-2 text-amber-500 flex-shrink-0" />

                        <span>Pickup: {formatDate(quote.pickup_date)}</span>
                      </div>

                      <div className="flex items-center text-gray-300">
                        <FiDollarSign className="mr-2 text-green-500 flex-shrink-0" />

                        <span>Status: {statusLabel(quote.status)}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      handleModalOpen(quote);
                    }}
                    className="w-full px-4 sm:px-6 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm hover:shadow"
                  >
                    {quote.has_load_transport ? "View / Edit Details" : "Review / Edit Quote"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}